import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {PorQue} from '@/components/ui/por-que'
import {cn} from '@/lib/utils'
import {ORDEN_SECCIONES, tonoDeSeccion, type AvanceSeccion, type SeccionClave} from '@/lib/bateria-cola'

const PUNTO_TONO: Record<'green' | 'amber' | 'neutral', string> = {
  green: 'bg-primary',
  amber: 'bg-tone-amber-fg',
  neutral: 'bg-border',
}

const TEXTO_TONO: Record<'green' | 'amber' | 'neutral', string> = {
  green: 'text-primary',
  amber: 'text-tone-amber-fg',
  neutral: 'text-muted-foreground',
}

/**
 * v2 §1.6. Todas las secciones con sus conteos desde el arranque, incluidas las vacías (R9).
 * En móvil va `fixed bottom-0`, nunca `top-0`, donde taparía el recorte.
 */
export function RielSecciones({
  avances,
  activa,
  navegable,
  sujeto,
  globalHechas,
  globalTotal,
  globalDejadas,
  globalEnConflicto,
  onIrASeccion,
}: {
  avances: AvanceSeccion[]
  activa: SeccionClave | null
  /** Informa desde el resumen pero solo navega con cola cargada: sin lista, el cursor quedaría en un cierre vacío. */
  navegable: boolean
  sujeto: string
  globalHechas: number
  globalTotal: number
  globalDejadas: number
  globalEnConflicto: number
  onIrASeccion: (clave: SeccionClave) => void
}) {
  const {t} = useTranslation()
  const [detalleAbierto, setDetalleAbierto] = useState(false)
  const indiceActiva = activa ? ORDEN_SECCIONES.indexOf(activa) + 1 : ORDEN_SECCIONES.length

  return (
    <nav
      aria-label={t('admin.bateria.cola.riel.label')}
      data-testid="cola-riel"
      className={cn(
        'surface px-4 py-3',
        'max-md:fixed max-md:inset-x-0 max-md:bottom-0 max-md:z-40 max-md:rounded-none max-md:border-t max-md:px-3 max-md:py-2'
      )}>
      <div className="flex items-start gap-2">
        <div className="grid w-full min-w-0 grid-cols-6 gap-1 text-left">
          {avances.map((avance) => {
            const tono = tonoDeSeccion(avance)
            const esActiva = avance.clave === activa
            return (
              <button
                key={avance.clave}
                type="button"
                disabled={!navegable || avance.total === 0}
                onClick={() => onIrASeccion(avance.clave)}
                aria-current={esActiva ? 'step' : undefined}
                className="min-w-0 space-y-1 rounded-md text-left focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none enabled:cursor-pointer disabled:cursor-default"
                data-testid={`cola-riel-${avance.clave}`}>
                <span className="flex items-start gap-1.5">
                  <span aria-hidden="true" className={cn('mt-1 size-2 shrink-0 rounded-full', PUNTO_TONO[tono])} />
                  {/* Sin `truncate`: un rótulo cortado deja la pregunta sin responder. */}
                  <span
                    className={cn(
                      'font-mono text-[10px] leading-tight tracking-wider uppercase',
                      esActiva ? 'text-foreground' : 'text-muted-foreground'
                    )}>
                    {t(`admin.bateria.cola.seccion.${avance.clave}`)}
                  </span>
                </span>
                <span className={cn('block font-mono text-[11px]', TEXTO_TONO[tono])}>
                  {avance.total === 0
                    ? t('admin.bateria.cola.riel.sinNada')
                    : t('admin.bateria.cola.riel.conteo', {hechas: avance.hechas, total: avance.total})}
                </span>
              </button>
            )
          })}
        </div>
        <button
          type="button"
          data-testid="cola-riel-detalle"
          aria-expanded={detalleAbierto}
          aria-label={t('admin.bateria.cola.riel.detalle')}
          onClick={() => setDetalleAbierto((abierto) => !abierto)}
          className="shrink-0 font-mono text-[11px] text-muted-foreground md:hidden">
          {detalleAbierto ? '▾' : '▸'}
        </button>
        <PorQue
          label={t('admin.bateria.cola.riel.porQueTandasLabel')}
          testId="cola-riel-ayuda"
          contenidoTestId="cola-riel-ayuda-contenido">
          {t('admin.bateria.cola.riel.porQueTandas')}
        </PorQue>
      </div>

      <p
        className={cn(
          'mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground',
          'max-md:mt-1',
          !detalleAbierto && 'max-md:hidden'
        )}
        data-testid="cola-contador-global">
        <span>{t('admin.bateria.cola.riel.posicion', {n: indiceActiva, total: ORDEN_SECCIONES.length})}</span>
        <span className="font-mono">
          {t('admin.bateria.cola.riel.global', {hechas: globalHechas, total: globalTotal, sujeto})}
        </span>
        {globalDejadas > 0 && (
          <span className="text-tone-amber-fg" data-testid="cola-dejadas">
            {t('admin.bateria.cola.riel.dejadas', {count: globalDejadas})}
          </span>
        )}
        {globalEnConflicto > 0 && (
          <span className="text-tone-amber-fg" data-testid="cola-riel-conflictos">
            {t('admin.bateria.cola.riel.enConflicto', {count: globalEnConflicto})}
          </span>
        )}
      </p>
    </nav>
  )
}
